import {
  WSSelectOption,
  WSSelectSearch,
  WSSelectSearchProps
} from "@wingspanhq/fe-component-library";
import {
  Payer,
  PayerEngagement,
  PayerEngagementStatus,
  PayerRowEngagement,
  PayerRowsApiSearchPayerRowsRequest
} from "@wingspanhq/wingspan-api-sdk";
import { useCallback, useEffect, useMemo, useState } from "react";
import { queriesPayerEngagements, queriesPayerRows } from "@query";
import { selectorSearchRowName } from "../../../../shared/selectors/selectorSearchRowName";
import { useAddClientModal } from "../AddClientModal/useAddClientModal";

type Props = Omit<WSSelectSearchProps, "options"> & {
  defaultSearchText?: string;
  onAddNewPayer: (payer: Payer, engagements: PayerEngagement[]) => void;
  baseRequest?: PayerRowsApiSearchPayerRowsRequest;
  /**
   * Narrows each row's engagements after they arrive, for rules better kept out
   * of the search request: an `engagements.*` filter there drops the whole
   * client row, so what the picker can offer ends up depending on the index
   * answering that filter. With this the request fetches every client and the
   * rule is applied to what came back. Pass a stable reference (module scope or
   * `useCallback`).
   */
  engagementFilter?: (engagement: PayerRowEngagement) => boolean;
  menuFooterText?: string;
  hideMenuFooterAction?: boolean;
  customMenuFooterAction?: WSSelectSearchProps["menuFooterAction"];
};

/**
 * A client engagement is billable as soon as it exists — v3 creates it `Created`
 * and activates it when the client accepts the link request, and v1 allowed
 * invoicing a client that had not accepted. Same set as
 * `SelectEngagementForPayer`.
 */
const SELECTABLE_STATUSES: PayerEngagementStatus[] = [
  PayerEngagementStatus.CREATED,
  PayerEngagementStatus.ACTIVATED
];

export const SelectPayerEngagement: React.FC<Props> = ({
  defaultSearchText,
  onAddNewPayer,
  baseRequest,
  engagementFilter,
  menuFooterText,
  hideMenuFooterAction,
  customMenuFooterAction,
  ...otherProps
}) => {
  const [searchText, setSearchText] = useState(defaultSearchText);
  const queryPayerRows = queriesPayerRows.searchPayerRows.usePaginatedQuery({
    ...baseRequest,
    filter: {
      ...baseRequest?.filter,
      searchString: searchText ? { eq: searchText } : undefined
    }
  });

  const onReachMenuEnd = useCallback(() => {
    if (queryPayerRows.hasNextPage) {
      queryPayerRows.fetchNextPage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryPayerRows.hasNextPage]);

  const modalAddClient = useAddClientModal();

  const options = useMemo(() => {
    const options: WSSelectOption[] = [];

    (queryPayerRows.flatData ?? []).forEach(payer => {
      const name = selectorSearchRowName(payer);

      let filteredEngagements = (payer.engagements ?? []).filter(e =>
        SELECTABLE_STATUSES.includes(e.status!)
      );

      // The caller's rule (e.g. work logging) runs here rather than in the
      // search request, so a rule the index answers badly cannot drop clients.
      if (engagementFilter) {
        filteredEngagements = filteredEngagements.filter(engagementFilter);
      }

      filteredEngagements.forEach(e => {
        options.push({
          value: payer.id + "/" + e.id,
          // Default engagements have no distinct name to show alongside the payer.
          label: [name, e.isDefault ? undefined : e.name]
            .filter(Boolean)
            .join(" • "),
          labels: payer.email ? [payer.email] : [],
          avatar: {
            type: "text",
            text: name
          }
        });
      });
    });

    return options;
  }, [queryPayerRows.flatData, engagementFilter]);

  // Filtering after the fetch can empty a whole page, and an empty menu has
  // nothing to scroll — `onReachMenuEnd` would never fire and the picker would
  // look empty while later pages still hold matches. So keep pulling pages until
  // something is on offer or the list runs out.
  const { hasNextPage, isFetching, fetchNextPage } = queryPayerRows;

  useEffect(() => {
    if (
      engagementFilter &&
      options.length === 0 &&
      hasNextPage &&
      !isFetching
    ) {
      fetchNextPage();
    }
  }, [
    engagementFilter,
    options.length,
    hasNextPage,
    isFetching,
    fetchNextPage
  ]);

  return (
    <WSSelectSearch
      {...otherProps}
      // label="Payer"
      placeholder="Select an option"
      options={options}
      status={queryPayerRows.isInitialLoading ? "loading" : otherProps.status}
      externalSearchText={searchText}
      onExternalSearchChange={setSearchText}
      isListLoading={
        queryPayerRows.isFetching && !queryPayerRows.isFetchingNextPage
      }
      onReachMenuEnd={onReachMenuEnd}
      menuFooterText={
        menuFooterText ??
        "Don't see a Client name? Add a new client to your client list."
      }
      menuFooterAction={
        hideMenuFooterAction
          ? undefined
          : (customMenuFooterAction ?? {
              label: "Add client",
              onClick: async () => {
                const payer = await modalAddClient.open();
                if (payer) {
                  const engagements =
                    await queriesPayerEngagements.listPayerEngagements.fetchQueryData(
                      { payerId: payer.id! }
                    );
                  onAddNewPayer(payer, engagements.data ?? []);
                }
              }
            })
      }
    />
  );
};
